import React, { useState } from "react";
import { Heart, Zap, Moon, Droplet, Plus, Minus } from "lucide-react";
import { Input } from "../components/ui/input";
import { saveTrackers } from "../lib/storage";
import { getInsight } from "../lib/insights";

const MOOD_EMOJI = ["😞", "😕", "😐", "🙂", "😊"];
const ENERGY_DOTS = [1, 2, 3, 4, 5];

const Trackers = ({ initial, isProUser, onChange }) => {
  const [mood, setMood] = useState(initial.mood);
  const [energy, setEnergy] = useState(initial.energy);
  const [sleep, setSleep] = useState(initial.sleep || "");
  const [hydration, setHydration] = useState(initial.hydration || 0);

  const update = (next) => {
    const merged = { mood, energy, sleep, hydration, ...next };
    setMood(merged.mood);
    setEnergy(merged.energy);
    setSleep(merged.sleep);
    setHydration(merged.hydration);
    saveTrackers(merged);
    if (onChange) onChange(merged);
  };

  const insight = getInsight({ mood, energy, sleep, hydration }, isProUser);

  return (
    <section className="px-6 py-6 space-y-4" data-testid="trackers-section">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38]">Daily Check-in</p>
        <h2 className="text-2xl font-medium text-[#1A2E24] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          How is today?
        </h2>
      </div>

      {/* Mood */}
      <div className="rounded-3xl bg-white border border-[#E2EBE6] p-5 shadow-[0_8px_32px_rgba(35,75,56,0.06)]" data-testid="mood-tracker">
        <div className="flex items-center gap-2 text-[#234B38]">
          <Heart className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Mood</span>
        </div>
        <div className="flex items-center justify-between mt-4">
          {MOOD_EMOJI.map((e, i) => {
            const active = mood === i + 1;
            return (
              <button
                key={i}
                data-testid={`mood-${i + 1}`}
                onClick={() => update({ mood: i + 1 })}
                className={`text-3xl transition-all no-tap-highlight ${
                  active ? "scale-125 opacity-100" : "opacity-40 hover:opacity-80 hover:scale-110"
                }`}
                aria-label={`Mood ${i + 1}`}
              >
                {e}
              </button>
            );
          })}
        </div>
      </div>

      {/* Energy */}
      <div className="rounded-3xl bg-white border border-[#E2EBE6] p-5 shadow-[0_8px_32px_rgba(35,75,56,0.06)]" data-testid="energy-tracker">
        <div className="flex items-center gap-2 text-[#234B38]">
          <Zap className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Energy</span>
        </div>
        <div className="flex items-center justify-between mt-4 gap-2">
          {ENERGY_DOTS.map((n) => {
            const active = energy !== null && n <= energy;
            return (
              <button
                key={n}
                data-testid={`energy-${n}`}
                onClick={() => update({ energy: n })}
                className={`flex-1 h-8 rounded-full transition-colors no-tap-highlight ${
                  active ? "bg-[#234B38]" : "bg-[#E2EBE6]"
                }`}
                aria-label={`Energy ${n}`}
              />
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-[#5C7568] mt-2 uppercase tracking-wider">
          <span>Low</span>
          <span>High</span>
        </div>
      </div>

      {/* Sleep */}
      <div className="rounded-3xl bg-white border border-[#E2EBE6] p-5 shadow-[0_8px_32px_rgba(35,75,56,0.06)]" data-testid="sleep-tracker">
        <div className="flex items-center gap-2 text-[#234B38]">
          <Moon className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Sleep</span>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Input
            data-testid="sleep-input"
            type="number"
            inputMode="decimal"
            min="0"
            max="14"
            step="0.5"
            value={sleep}
            onChange={(e) => update({ sleep: e.target.value })}
            placeholder="Hours"
            className="rounded-2xl border-[#E2EBE6] bg-[#FDFBF7] focus-visible:ring-[#234B38] flex-1"
          />
          <span className="text-sm text-[#5C7568]">hours</span>
        </div>
      </div>

      {/* Hydration */}
      <div className="rounded-3xl bg-white border border-[#E2EBE6] p-5 shadow-[0_8px_32px_rgba(35,75,56,0.06)]" data-testid="hydration-tracker">
        <div className="flex items-center gap-2 text-[#234B38]">
          <Droplet className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Hydration</span>
        </div>
        <div className="flex items-center justify-between mt-4">
          <button
            data-testid="hydration-decrease"
            onClick={() => update({ hydration: Math.max(0, hydration - 1) })}
            className="w-10 h-10 rounded-full border border-[#E2EBE6] flex items-center justify-center hover:bg-[#E2EBE6]/40 transition-colors no-tap-highlight"
            aria-label="Decrease hydration"
          >
            <Minus className="w-4 h-4 text-[#234B38]" />
          </button>
          <div className="flex items-center gap-1.5">
            {Array.from({ length: 8 }).map((_, i) => (
              <Droplet
                key={i}
                className={`w-5 h-5 ${i < hydration ? "text-[#234B38] fill-[#234B38]" : "text-[#E2EBE6]"}`}
                strokeWidth={1.5}
              />
            ))}
          </div>
          <button
            data-testid="hydration-increase"
            onClick={() => update({ hydration: Math.min(20, hydration + 1) })}
            className="w-10 h-10 rounded-full bg-[#234B38] flex items-center justify-center hover:bg-[#1A382A] transition-colors no-tap-highlight"
            aria-label="Increase hydration"
          >
            <Plus className="w-4 h-4 text-white" />
          </button>
        </div>
        <p className="text-xs text-[#5C7568] mt-2 text-center" data-testid="hydration-count">
          {hydration} glass{hydration === 1 ? "" : "es"}
        </p>
      </div>

      {insight && (
        <div className="rounded-3xl bg-gradient-to-br from-[#234B38]/5 to-[#F5D0D6]/30 border border-[#E2EBE6] p-5" data-testid="insight-card">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#234B38] mb-2">Insight</p>
          <p className="text-sm leading-relaxed text-[#1A2E24]" data-testid="insight-text">
            {insight}
          </p>
          {!isProUser && (
            <p className="text-[11px] text-[#5C7568] mt-3 italic">
              Pro unlocks deeper, multi-signal insights.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default Trackers;
